import {
  Box,
  Typography,
  Container,
  IconButton,
  Button,
  Card,
  CardMedia,
  CardContent,
  Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import config from '../../Config';

export default function SubcategoryScreen() {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const propertyId = sessionStorage.getItem('propertyDocumentID');
  const location = useLocation();
  const hasSubCategory = location.state?.hasSubCategory ?? false;
  const [subCategories, setSubCategories] = useState([]);
  const [bannerImage, setBannerImage] = useState('');
  const [productsByKey, setProductsByKey] = useState({});
  const [expandedKey, setExpandedKey] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  console.log(hasSubCategory);

  useEffect(() => {
    if (!propertyId || !categoryId) return;

    const fetchData = async () => {
      setLoading(true);

      try {
        if (hasSubCategory) {
          const res = await fetch(
            `${config.apiBaseUrl}/guest/upsale/property/${propertyId}/categories/${categoryId}/subcategories`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!res.ok) throw new Error('Failed to fetch subcategories');

          const data = await res.json();
          setCategoryName(data.categoryName || 'Category');

          if (Array.isArray(data.subCategories) && data.subCategories.length > 0) {
            setSubCategories(data.subCategories);
            setBannerImage(data.subCategories[0]?.image || '');
          } else {
            await fetchProducts('category');
          }
        } else {
          await fetchProducts('category');
        }
      } catch (error) {
        setCategoryName('Category');
      } finally {
        setLoading(false);
      }
    };

    const fetchProducts = async (key) => {
      setLoadingKey(key);
      try {
        const res = await fetch(
          `${config.apiBaseUrl}/guest/upsale/property/${propertyId}/products?categoryId=${categoryId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) throw new Error('Failed to fetch products');

        const data = await res.json();
        setProductsByKey((prev) => ({
          ...prev,
          [key]: data.products || [],
        }));
        setExpandedKey(key);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingKey(null);
      }
    };

    fetchData();
  }, [propertyId, categoryId, hasSubCategory, token]);

  const handleBackClick = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/top-picks');
  };

  const handleOptionClick = async (subcategory) => {
    const key = subcategory._id;

    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }

    setLoadingKey(key);

    try {
      const url = `${config.apiBaseUrl}/guest/upsale/property/${propertyId}/products?categoryId=${categoryId}&subCategoryId=${key}`;
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to fetch products');

      const data = await res.json();
      setProductsByKey((prev) => ({
        ...prev,
        [key]: data.products || [],
      }));
      setExpandedKey(key);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingKey(null);
    }
  };

  const renderProductCard = (item) => (
    <Card
      key={item._id}
      sx={{ display: 'flex', mb: 2, cursor: 'pointer' }}
      onClick={() => navigate(`/order/${item.product._id}`)}
    >
      <CardMedia component="img" image={item.product.image} sx={{ width: 100 }} />
      <CardContent>
        <Typography fontWeight={600}>{item.product.name}</Typography>
        <Typography color="primary">
          £{item.price} - {item.product.name}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '480px',
        margin: '0 auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          py: 2.5,
          px: 2,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fff',
          height: 60,
        }}
      >
        <IconButton sx={{ position: 'absolute', left: 16 }} onClick={handleBackClick}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {loading ? <Skeleton width={160} /> : categoryName}
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, px: 3, py: 3 }}>
        {/* Banner */}
        <Box
          sx={{
            borderRadius: '16px',
            overflow: 'hidden',
            height: '220px',
            backgroundColor: '#d1d1d1',
            mb: 4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          {loading ? (
            <Skeleton variant="rectangular" width="100%" height="100%" />
          ) : bannerImage ? (
            <Box
              component="img"
              src={bannerImage}
              alt="Banner"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : null}
        </Box>

        <Typography variant="h6" fontWeight={700} mb={2}>
          Choose Your Favorite
        </Typography>

        {subCategories.length > 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            {subCategories.map((option) => {
              const key = option._id;
              const isExpanded = expandedKey === key;
              const isLoading = loadingKey === key;
              const products = productsByKey[key] || [];

              return (
                <Box key={key}>
                  <Button
                    fullWidth
                    onClick={() => handleOptionClick(option)}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: '#fff',
                      py: 2,
                      px: 3,
                      borderRadius: '12px',
                      textTransform: 'none',
                      justifyContent: 'space-between',
                    }}
                    endIcon={
                      isLoading ? (
                        <Skeleton variant="circular" width={20} height={20} />
                      ) : isExpanded ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <ChevronRightIcon />
                      )
                    }
                  >
                    {option.name}
                  </Button>

                  {isExpanded && <Box mt={2}>{products.map(renderProductCard)}</Box>}
                </Box>
              );
            })}
          </Box>
        )}

        {subCategories.length === 0 && expandedKey === 'category' && (
          <Box mt={2}>{(productsByKey['category'] || []).map(renderProductCard)}</Box>
        )}
      </Container>
    </Box>
  );
}
